/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react'
import classNames from 'classnames'
import { FilterData } from '../../typings/selectors'
import * as R from 'ramda'

// app components
import withFilterData from '../decorators/filterData'
import Form, { FormProps } from '../inc/Form'

// type definition
export type GrabFilterData = {
    listId: string
    dataFilters?: Record<string, any>
    onFilter?: (filters: Record<string, any>) => void
    onChange?: (event: Event) => void
    onSubmit?: FormProps['onSubmit']
    isFilter?: boolean
    filterActions: {
        showByStates(state: object, t?: number): void | Promise<any>
        showByResetStates?(state: object, t?: number): void | Promise<any>
        toggleFilterItem?(state: string, value: any): void | Promise<any>
        resetResults?(results: any[]): void | Promise<any>
    }
}

type DataFilterProps = GrabFilterData &
    FilterData &
    Omit<FormProps, 'formRef' | 'onChange' | 'onSubmit'>

type DataFilterState = {
    isSearching: boolean
    filters: NonNullable<GrabFilterData['dataFilters']>
}

type DataFilterDefaults = Required<Pick<DataFilterProps, 'isLoading' | 'filters' | 'dataFilters'>>

class DataFilter extends React.Component<DataFilterProps, DataFilterState> {
    public static defaultProps: DataFilterDefaults = {
        isLoading: false,
        filters: {},
        dataFilters: {}
    }

    // initial state
    public state = {
        isSearching: false,
        filters: this.props.dataFilters || this.props.filters || {}
    }

    // initial attributes
    private lastSearch: any = null
    private formEl: HTMLFormElement | null = null

    public componentDidMount(): void {
        if (this.formEl instanceof HTMLFormElement) {
            const cleaners = this.formEl.querySelectorAll('.on-clean-filters')

            cleaners.forEach(cleanEl => {
                cleanEl.addEventListener('click', this._handleClean, false)
            })

            this.formEl.addEventListener('change', this._handleChange, true)
        }
    }

    public componentWillUnmount(): void {
        if (this.formEl instanceof HTMLFormElement) {
            const cleaners = this.formEl.querySelectorAll('.on-clean-filters')

            cleaners.forEach(cleanEl => {
                cleanEl.removeEventListener('click', this._handleClean, false)
            })

            this.formEl.removeEventListener('change', this._handleChange, true)
        }
    }

    private _handleClean = (event: Event): void => {
        event.preventDefault()
        const { dataFilters } = this.props
        const cleanFilters = { ...this.state.filters }

        // reset fields
        // this.formObj.resetFields()

        for (const n in cleanFilters) {
            cleanFilters[n] = undefined
        }

        this.updateFilters({ ...dataFilters, ...cleanFilters })
    }

    private _handleChange = (event: Event): void => {
        const field = event.target as HTMLInputElement

        if (field.tagName.toLowerCase() === 'input' && field.type === 'hidden') {
            const fieldForm = field.form
            const submitEl =
                fieldForm &&
                Array.from(fieldForm.querySelectorAll<HTMLInputElement>('[type=submit]')).find(
                    f => f.form === fieldForm
                )

            if (!submitEl) {
                this.handleAsyncChange(field, event.type)
            }

            if (typeof this.props.onChange === 'function') {
                this.props.onChange(event)
            }
        }
    }

    private handleChange = (
        event: React.BaseSyntheticEvent<Event, HTMLFormElement, HTMLInputElement>
    ): void => {
        event.persist()
        const field = event.target
        const fieldForm = field.form
        const submitEl =
            fieldForm &&
            Array.from(fieldForm.querySelectorAll<HTMLInputElement>('[type=submit]')).find(
                f => f.form === fieldForm
            )

        /* console.group('form filter::')
        console.dir(fieldForm)
        Array.from(fieldForm!.querySelectorAll<HTMLInputElement>('[type=submit]')).forEach(f => {
            console.log(f, f.form === fieldForm)
        })
        console.groupEnd() */

        if (!submitEl) {
            this.handleAsyncChange(field, event.nativeEvent.type)
        }

        if (typeof this.props.onChange === 'function') {
            this.props.onChange(event.nativeEvent)
        }
    }

    private handleSubmit = (values, actions): void => {
        const { onSubmit } = this.props
        const passValues = this.parseSubmittingValues(values)
        // console.log('Submitting::', passValues)

        this.updateFilters(passValues)

        if (typeof onSubmit === 'function') {
            onSubmit(passValues, actions)
        }
    }

    private async handleAsyncChange(field: HTMLInputElement, eventType: string): Promise<any> {
        const value = await this.verifyFieldValue(field, eventType)

        if (field.name) {
            if (!value || (Array.isArray(value) && !value.length)) {
                this.updateFilters(field.name, undefined)
            } else if (
                field.type === 'checkbox' &&
                typeof value !== 'boolean' &&
                !field.dataset.radiobox
            ) {
                this.updateFilters(field.name, value, true)
            } else {
                this.updateFilters(field.name, value)
            }
        }
    }

    private updateFilters(nameOrFilters: string | object, value?: any, toggle = false): void {
        const { filterActions, onFilter } = this.props
        const nextFilters =
            typeof nameOrFilters === 'object' ? nameOrFilters : { [nameOrFilters]: value }

        this.setState(
            prevState => {
                if (toggle && typeof nameOrFilters === 'string') {
                    const prevValue = Array.isArray(prevState.filters[nameOrFilters])
                        ? [...prevState.filters[nameOrFilters]]
                        : []

                    if (prevValue.indexOf(value) !== -1) {
                        prevValue.splice(prevValue.indexOf(value), 1)
                    } else {
                        prevValue.push(value)
                    }

                    nextFilters[nameOrFilters] = prevValue.length ? prevValue : undefined
                }
                filterActions.showByStates(nextFilters)

                return {
                    filters: {
                        ...prevState.filters,
                        ...nextFilters
                    }
                }
            },
            // callback when set state
            () => {
                // run filter callback
                if (typeof onFilter === 'function') {
                    onFilter(this.state.filters)
                }
            }
        )
    }

    private verifyFieldValue(field: HTMLInputElement, eventType: string): Promise<any> {
        let value: any = field.value
        // console.log('Verify Field::', field.type, field.value || field.checked, eventType)

        // verify the checkbox when they are unchecked
        if (
            field.type === 'checkbox' &&
            (['true', 'false'].indexOf(field.value) !== -1 || !field.value)
        ) {
            value = field.checked
        } else if (eventType === 'input') {
            // delay the change value when the value was typed
            if (this.lastSearch !== null) {
                clearTimeout(this.lastSearch)
            }

            return new Promise((resolve, reject) => {
                if ((value as string).trim().length > 3 || value === '') {
                    this.lastSearch = setTimeout(() => {
                        resolve(value)
                    }, 700)
                } else {
                    reject('')
                }
            })
        } else if (field.type === 'hidden' && this.formEl instanceof HTMLFormElement) {
            const altValue: any[] = Array.from(
                this.formEl.querySelectorAll<HTMLInputElement>(`[name=${field.name}]`)
            ).map(el => el.value)

            value = altValue.filter(v => !!v && v !== 0)
        }

        return Promise.resolve(value)
    }

    private parseSubmittingValues(values: object): Record<string, any> {
        const returnValues = {}

        for (const [key, value] of Object.entries(values)) {
            if (Array.isArray(value) && value.length) {
                const altValue = value.map(opt =>
                    opt && Object.hasOwnProperty.call(opt, 'value') ? opt.value : opt
                )

                returnValues[key] = altValue.filter(v => !!v && v !== 0)
            } else if (!value || R.isEmpty(value)) {
                returnValues[key] = undefined
            } else {
                returnValues[key] = value
            }
        }

        return returnValues
    }

    public render(): React.ReactNode {
        const { children, className, ...props } = R.omit(
            [
                'listId',
                'filterActions',
                'pageActions',
                'dataFilters',
                'isLoading',
                'onSubmit',
                'onChange',
                'onFilter',
                'filters',
                'stale',
                'sort',
                'sortReverse'
            ],
            this.props
        )
        const classes = classNames(className, 'data-filter-form')

        return (
            <Form
                className={classes}
                onChange={this.handleChange}
                onSubmit={this.handleSubmit}
                formRef={formNode => {
                    this.formEl = formNode
                }}
                {...props}
            >
                {children}
            </Form>
        )
    }
}

export default withFilterData(DataFilter)
